import React, { createContext, useContext, useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiFetch } from '../../../utils/apiClient';
import { buildBlockedTasksMap } from '../../../utils/blockedTasks';
import useProjectWebSocket from '../../../hooks/useProjectWebSocket';

const ProjectDataContext = createContext(null);

export const useProjectData = () => {
    const context = useContext(ProjectDataContext);
    if (!context) {
        throw new Error('useProjectData must be used within a ProjectDataProvider');
    }
    return context;
};

export const ProjectDataProvider = ({ children }) => {
    const navigate = useNavigate();

    // Auth from localStorage
    const token = localStorage.getItem('clientToken');
    const email = localStorage.getItem('clientEmail');
    const leadId = localStorage.getItem('clientLeadId');
    const timestamp = localStorage.getItem('clientTimestamp');

    // Core project data
    const [loading, setLoading] = useState(true);
    const [project, setProject] = useState(null);
    const [tasks, setTasks] = useState([]);
    const [columns, setColumns] = useState([]);
    const [taskStatuses, setTaskStatuses] = useState([]);
    const [epics, setEpics] = useState([]);
    const [members, setMembers] = useState([]);
    const [lead, setLead] = useState(null);
    // Read inside the WebSocket handler, which must not re-subscribe whenever
    // the lead object changes (LIV-15a).
    const leadRef = useRef(null);
    const [teamSwimlaneId, setTeamSwimlaneId] = useState(null);
    const [links, setLinks] = useState([]);
    const [invalidDeps, setInvalidDeps] = useState([]);
    const [error, setError] = useState('');
    const [lastUpdate, setLastUpdate] = useState(Date.now());
    const [risksLastUpdate, setRisksLastUpdate] = useState(Date.now());
    const [issuesLastUpdate, setIssuesLastUpdate] = useState(Date.now());
    // LIV-15: the Team page's refresh signal, exposed exactly as the two above
    // are. It is bumped by `team_updated` (a role change) and by the debounced
    // `project_updated` (an invite, a removal, a disable or an enable, all of
    // which write the members row rather than raising team_updated).
    const [teamLastUpdate, setTeamLastUpdate] = useState(Date.now());
    const [issues, setIssues] = useState([]);
    const [risks, setRisks] = useState([]);
    // Distinguishes "risks have not arrived yet" from "there are no risks". The
    // page frame renders on `loading`, which follows fetchProjectData alone, so
    // risks can land a moment later and an empty array is ambiguous until then.
    const [risksLoaded, setRisksLoaded] = useState(false);

    // Refs
    const updateInProgressRef = useRef(false);
    const selectedTaskRef = useRef(null);
    const preventRefreshRef = useRef(false);
    const projectIdRef = useRef(null);
    const batchInProgressRef = useRef(false); // Ignore updates during admin batch operations
    const fetchInProgressRef = useRef(false); // Prevent concurrent fetches
    const refreshDebounceRef = useRef(null); // Debounce rapid WebSocket notifications

    // Build weekendWork map from tasks' weekendDatesWorked fields
    const weekendWork = useMemo(() => {
        const map = {};
        for (const task of tasks) {
            if (task.weekendDatesWorked && Object.keys(task.weekendDatesWorked).length > 0) {
                map[task.taskId] = task.weekendDatesWorked;
            }
        }
        return map;
    }, [tasks]);

    // Fetch issues (for blocked tasks map)
    const fetchIssues = useCallback(async () => {
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/issues`
            );
            if (response.ok) {
                const result = await response.json();
                setIssues(result.issues || []);
            }
        } catch (err) {
            console.error('Failed to fetch issues:', err);
        }
    }, []);

    // Fetch risks (for the client Risks page). No lead gate: /client/risks/all
    // takes no parameters, resolving the project from the verified JWT, so
    // waiting for `lead` only delayed the request behind the project fetch.
    // No showLoading parameter either - the tables call onRefresh(false) and the
    // argument is simply ignored, which is the wanted silent-refresh behaviour.
    const fetchRisks = useCallback(async () => {
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/risks/all`
            );
            if (response.ok) {
                const result = await response.json();
                setRisks(result.risks || []);
            }
        } catch (err) {
            console.error('Failed to fetch risks:', err);
        } finally {
            setRisksLoaded(true);
        }
    }, []);

    // Main project data fetch
    const fetchProjectData = useCallback(async () => {
        if (!token || !email || !leadId || !timestamp) {
            navigate('/client/login');
            return;
        }
        try {
            const response = await apiFetch(
                `${process.env.REACT_APP_API_URL}/client/project?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`
            );
            if (response.ok) {
                const data = await response.json();

                setProject(data.project);
                setMembers(data.members || []);

                const filteredTasks = (data.tasks || []).filter(task =>
                    task.projectId === data.project.projectId
                );
                setTasks(filteredTasks);

                if (data.project?.epics) {
                    setColumns(data.project.epics);
                }

                if (data.project?.subtaskStatuses) {
                    setTaskStatuses(data.project.subtaskStatuses);
                }

                if (data.epics?.length > 0) {
                    setEpics(data.epics);
                }

                if (data.links) {
                    setLinks(data.links);
                }
                setInvalidDeps(data.invalidDeps || []);

                if (data.project?.swimlanes) {
                    const teamSwimlane = data.project.swimlanes.find(s => s.name === 'Team');
                    if (teamSwimlane) {
                        setTeamSwimlaneId(teamSwimlane.swimlaneId);
                    }
                }

                // Track project ID changes
                if (data.project?.projectId && data.project.projectId !== projectIdRef.current) {
                    projectIdRef.current = data.project.projectId;
                }

                // Fetch lead data
                try {
                    const leadResponse = await apiFetch(
                        `${process.env.REACT_APP_API_URL}/client/lead?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`
                    );
                    if (leadResponse.ok) {
                        const leadData = await leadResponse.json();
                        setLead(leadData);
                    }
                } catch (err) {
                    console.error('Failed to load lead data:', err);
                }

                setError('');
                setLastUpdate(Date.now());
            } else {
                setError('Failed to load project data');
            }
        } catch (err) {
            console.error('Fetch error:', err);
            setError('Connection error. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [email, token, leadId, timestamp, navigate]);

    // Initial fetch and WebSocket setup
    useEffect(() => {
        if (!token || !email || !leadId || !timestamp) {
            navigate('/client/login');
            return;
        }

        fetchProjectData();
        fetchIssues();
        fetchRisks();
    }, [token, email, leadId, timestamp, navigate, fetchProjectData, fetchIssues, fetchRisks]);

    // A notification landing while a fetch is IN FLIGHT must not be dropped:
    // the running fetch read the tables before those writes committed, so
    // skipping the refetch left the portal one change behind until the NEXT
    // write arrived (2026-08-23, surfaced by a 417-task project making the
    // fetch take seconds). Queue exactly one follow-up and run it when the
    // current fetch settles - never drop, never stack more than one.
    const pendingRefetchRef = useRef(false);
    const runGuardedFetch = useCallback(function run() {
        if (fetchInProgressRef.current) {
            pendingRefetchRef.current = true;
            return;
        }
        fetchInProgressRef.current = true;
        fetchProjectData().finally(() => {
            fetchInProgressRef.current = false;
            if (pendingRefetchRef.current) {
                pendingRefetchRef.current = false;
                run();
            }
        });
    }, [fetchProjectData]);

    useEffect(() => {
        leadRef.current = lead;
    }, [lead]);

    // WebSocket message handling. The connection itself is owned by
    // useProjectWebSocket below; this stays here because the batch
    // suppression, debouncing and fetch guards are all local state.
    const handleWebSocketMessage = useCallback((message) => {
        if (message.type === 'pong') {
            return;
        }

        if (message.type === 'batch_start') {
            console.log('Batch operation started - ignoring updates');
            batchInProgressRef.current = true;
            return;
        }

        if (message.type === 'batch_end') {
            console.log('Batch operation ended - refreshing');
            batchInProgressRef.current = false;
            runGuardedFetch();
            return;
        }

        if (message.type === 'project_updated') {
            console.log('Project updated via WebSocket');
            // Skip refresh if batch operation in progress or local operation in progress
            if (!preventRefreshRef.current && !batchInProgressRef.current) {
                // Debounce refresh to handle rapid notifications (e.g., both weekend days)
                if (refreshDebounceRef.current) {
                    clearTimeout(refreshDebounceRef.current);
                }
                refreshDebounceRef.current = setTimeout(() => {
                    runGuardedFetch();
                    // LIV-15: an invite, a removal, a disable or an enable
                    // writes the MEMBERS row and arrives here, not as
                    // team_updated - which has exactly one emitter, the
                    // role-change path. The Team page must react to both or F3
                    // is fixed for role changes only and looks done. Riding
                    // this existing 500ms debounce rather than adding a second
                    // timer to the context.
                    setTeamLastUpdate(Date.now());
                }, 500);
            }
        }

        if (message.type === 'team_updated') {
            // D8 - GENTLE REFRESH, RELOAD ONLY FOR THE PERSON AFFECTED.
            //
            // This used to be an unconditional window.location.reload(): the
            // signal DID reach the Team page, by destroying the whole portal
            // and rebuilding it. The reload is not arbitrary, though - a user
            // whose OWN role just changed must have their permissions
            // re-derived, and a reload guarantees that.
            //
            // ⚠️ The message carries no member id, and the emitter is NOT
            // changed to add one: it is the third notification mechanism (a
            // Lambda invoke) and this plan does not touch it. So the question
            // "was it me?" is asked directly instead.
            //
            // ⛔ THE ROUTE IS `/client/lead`, AND THE FIELD IS `clientRole`.
            // That is the value the portal actually gates on
            // (ClientPortalLayout), this context already fetches that route,
            // and it is served live from Cognito - verifyTokenAndGetLead reads
            // `custom:clientRole` off the account, so it is never a stored
            // copy. The roster route would cost one AdminGetUser per member and
            // then need a self-lookup, to answer a question about one value.
            //
            // ⛔ FAILING TOWARDS A RELOAD is the only safe direction when the
            // question is "do my permissions still hold".
            (async () => {
                const held = leadRef.current?.clientRole;
                try {
                    const response = await apiFetch(
                        `${process.env.REACT_APP_API_URL}/client/lead?leadId=${encodeURIComponent(leadId)}&timestamp=${encodeURIComponent(timestamp)}`
                    );
                    if (!response.ok) {
                        window.location.reload();
                        return;
                    }
                    const fresh = await response.json();
                    if (fresh.clientRole !== held) {
                        console.log('Your own role changed - reloading to re-derive permissions');
                        window.location.reload();
                        return;
                    }
                    setLead(fresh);
                    setTeamLastUpdate(Date.now());
                } catch (err) {
                    console.error('Failed to re-check the signed-in role:', err);
                    window.location.reload();
                }
            })();
        }

        if (message.type === 'risks_updated') {
            console.log('Risks updated via WebSocket');
            setRisksLastUpdate(Date.now());
        }

        if (message.type === 'issues_updated') {
            console.log('Issues updated via WebSocket');
            setIssuesLastUpdate(Date.now());
        }
        // `leadId` and `timestamp` are read by the team_updated branch (LIV-15a).
        // Both are localStorage strings read on every render, so listing them is
        // free - the same string each time keeps this callback's identity stable
        // - and it clears the exhaustive-deps warning the branch introduced.
        // useProjectWebSocket holds the handler in a ref, so even a new identity
        // would never reconnect the socket.
    }, [runGuardedFetch, leadId, timestamp]);

    // Ping every 5 minutes, reconnect after 3 seconds, first connect after
    // 100ms - the values this context has always used, kept deliberately
    // rather than adopting the hook's defaults.
    useProjectWebSocket({
        leadId,
        timestamp,
        // getToken, not a captured token: reconnects hours in must carry the
        // CURRENT token or the authorizer refuses them forever (BUG-030)
        getToken: () => localStorage.getItem('clientToken'),
        onMessage: handleWebSocketMessage,
        enabled: !!(token && email && leadId && timestamp),
        pingIntervalMs: 5 * 60 * 1000,
        reconnectDelayMs: 3000,
        connectDelayMs: 100
    });

    // The debounce timer belongs to the message handler, not the connection,
    // so it is still cleared here
    useEffect(() => {
        return () => {
            if (refreshDebounceRef.current) {
                clearTimeout(refreshDebounceRef.current);
            }
        };
    }, []);

    // Refresh issues when issuesLastUpdate changes (WebSocket notification).
    // The preventRefreshRef guard came from the copy of this effect that used to
    // live in Risks.js: IssuesTable sets that ref around an edit so a WebSocket
    // signal cannot refresh the table out from under it.
    const issuesInitialLoadRef = useRef(false);
    useEffect(() => {
        if (issuesInitialLoadRef.current && !preventRefreshRef.current) {
            fetchIssues();
        }
        issuesInitialLoadRef.current = true;
    }, [issuesLastUpdate, fetchIssues]);

    // Refresh risks when risksLastUpdate changes (WebSocket notification).
    // Same check-then-set shape as the issues effect above, which skips only the
    // mount run. Do not use the Risks.js pattern this replaced - it set its ref
    // in a separate effect, so the guard was already open on first run.
    const risksInitialLoadRef = useRef(false);
    useEffect(() => {
        if (risksInitialLoadRef.current && !preventRefreshRef.current) {
            fetchRisks();
        }
        risksInitialLoadRef.current = true;
    }, [risksLastUpdate, fetchRisks]);

    // Build blocked tasks map from issues and dependencies
    // Subtasks are tasks that have a mainTaskId field, mainTasks (main tasks) are tasks without mainTaskId
    const subtasks = useMemo(() => tasks.filter(t => t.mainTaskId), [tasks]);
    const mainTasks = useMemo(() => tasks.filter(t => !t.mainTaskId), [tasks]);
    const blockedTasksMap = useMemo(
        () => buildBlockedTasksMap(issues, links, subtasks, mainTasks),
        [issues, links, subtasks, mainTasks]
    );

    const value = {
        // Core data
        loading,
        project,
        tasks,
        setTasks,
        columns,
        taskStatuses,
        epics,
        members,
        lead,
        teamSwimlaneId,
        links,
        invalidDeps,
        error,
        setError,
        fetchProjectData,
        lastUpdate,
        risksLastUpdate,
        issuesLastUpdate,
        teamLastUpdate,
        issues,
        // Exposed so the Risks page can refresh issues in place rather than
        // keeping a second copy of them.
        fetchIssues,
        risks,
        fetchRisks,
        risksLoaded,
        blockedTasksMap,
        // Gantt data (weekendWork built from task.weekendDatesWorked)
        weekendWork,
        // Refs
        updateInProgressRef,
        selectedTaskRef,
        preventRefreshRef
    };

    return (
        <ProjectDataContext.Provider value={value}>
            {children}
        </ProjectDataContext.Provider>
    );
};

export default ProjectDataContext;
